<template>
  <div class="rounded-2xl border border-zinc-200 bg-surface p-6 transition hover:border-violet-500/30 dark:border-white/5 dark:bg-surface-dark dark:hover:border-violet-500/20">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0 flex-1">
        <h3 class="truncate text-base font-semibold text-zinc-900 dark:text-zinc-50">{{ story.title }}</h3>
        <time class="mt-1 block text-xs text-zinc-400">{{ formattedDate }}</time>
      </div>
      <button
        @click="toggleExpand"
        class="shrink-0 rounded-lg border border-zinc-200 p-1.5 text-zinc-400 transition hover:text-zinc-700 dark:border-white/10 dark:text-zinc-500 dark:hover:text-zinc-200"
      >
        <svg
          class="h-4 w-4 transition-transform"
          :class="{ 'rotate-180': expanded }"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>

    <!-- Tags -->
    <div class="mt-3 flex flex-wrap gap-1.5">
      <span class="rounded-full bg-violet-500/10 px-2.5 py-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">
        {{ story.selectedLanguage }}
      </span>
      <span class="rounded-full bg-indigo-500/10 px-2.5 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300">
        {{ story.selectedAudiance }}
      </span>
      <span class="rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
        {{ story.selectedGender }}
      </span>
      <span
        v-for="genre in story.selectedGenre"
        :key="genre"
        class="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-500 dark:bg-white/5 dark:text-zinc-400"
      >
        {{ genre }}
      </span>
    </div>

    <!-- Expanded body -->
    <div v-if="expanded" class="mt-4 space-y-4">
      <div
        class="rounded-xl bg-zinc-50 p-4 dark:bg-base-dark"
        :class="story.selectedLanguage === 'arabic' ? 'text-right' : 'text-left'"
        :dir="story.selectedLanguage === 'arabic' ? 'rtl' : 'ltr'"
      >
        <p class="whitespace-pre-wrap text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
          {{ story.story }}
        </p>
      </div>

      <button
        @click="toggleSpeech"
        class="inline-flex items-center gap-2 rounded-xl border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-500 transition hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:text-zinc-50"
      >
        <svg v-if="!isPlaying" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"/>
        </svg>
        <svg v-else class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5"/>
        </svg>
        {{ isPlaying ? 'Pause' : 'Listen' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';

const props = defineProps({
  story: { type: Object, required: true },
});

const expanded = ref(false);
const isPlaying = ref(false);
let utterance = null;

const formattedDate = computed(() => {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(props.story.timestamp));
});

function toggleExpand() {
  expanded.value = !expanded.value;
  if (!expanded.value) stopSpeech();
}

function toggleSpeech() {
  if (!utterance) {
    utterance = new SpeechSynthesisUtterance(props.story.story);
    utterance.lang = props.story.selectedLanguage === 'english' ? 'en-US' : 'ar-EG';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.onend = () => {
      isPlaying.value = false;
      utterance = null;
    };
    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);
    isPlaying.value = true;
  } else if (speechSynthesis.paused) {
    speechSynthesis.resume();
    isPlaying.value = true;
  } else {
    speechSynthesis.pause();
    isPlaying.value = false;
  }
}

function stopSpeech() {
  speechSynthesis.cancel();
  utterance = null;
  isPlaying.value = false;
}

onBeforeUnmount(() => stopSpeech());
</script>
